import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { ExternalLink, Copy, Check, Download, Smartphone, Tv, Zap, ShieldCheck, Sparkles, Film, Radio, Cast, MonitorPlay, Play } from 'lucide-react';
import api, { mediaUrl } from '../lib/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const DEFAULT_URL = 'https://goldentv123.github.io/GOLDEN-APP/';

// Icon registry - keeps unified look for any app
const ICON_MAP = { Tv, Zap, Sparkles, Film, Radio, Cast, MonitorPlay, Play, Smartphone, Download };
const getIcon = (name) => ICON_MAP[name] || Tv;

export default function GuidaDownload() {
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [urls, setUrls] = useState({});
  const [apps, setApps] = useState([]);
  const token = localStorage.getItem('gtc_token');

  useEffect(() => {
    api.get('/settings').then(({ data }) => {
      setCode(data.downloader_code || '');
      setUrls(data);
    }).catch(() => {});
    api.get('/download-apps').then(({ data }) => setApps(data)).catch(() => {});
  }, []);

  const copy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Codice copiato');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Impossibile copiare');
    }
  };

  return (
    <Layout title="Scarica le App" showBack backTo="/home">
      <div className="px-5 pt-6 max-w-md mx-auto space-y-6 pb-8">
        {/* Header */}
        <section>
          <div className="flex items-center gap-2 mb-2">
            <Smartphone className="w-5 h-5 text-[var(--gold)]" />
            <span className="text-xs uppercase tracking-[0.3em] text-white/40">Download Center</span>
          </div>
          <h2 className="font-heading text-2xl font-light">Tutto il necessario in un'unica schermata</h2>
          <p className="text-sm text-white/60 mt-2 leading-relaxed">
            Scarica le nostre app su <span className="text-white">Android</span> e <span className="text-white">Fire TV</span> usando il codice Downloader o aprendo la pagina di download ufficiale.
          </p>
        </section>

        {/* Downloader code card */}
        <section className="card-surface p-5 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[var(--gold)]/10 to-transparent pointer-events-none" />
          <div className="relative">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Codice Downloader</div>
            <div className="flex items-center gap-3">
              <div
                data-testid="downloader-code"
                className="font-heading text-3xl font-medium text-[var(--gold)] tracking-widest flex-1 truncate"
              >
                {code || '— — — — —'}
              </div>
              <button
                data-testid="copy-code-btn"
                onClick={copy}
                disabled={!code}
                className="p-3 rounded-full border border-[var(--border-subtle)] hover:border-[var(--gold)]/50 text-white/70 hover:text-[var(--gold)] transition-colors disabled:opacity-40 flex items-center gap-2 text-xs"
              >
                {copied ? <><Check className="w-4 h-4" /> Copiato</> : <><Copy className="w-4 h-4" /> Copia</>}
              </button>
            </div>
            {!code && (
              <p className="text-xs text-white/40 mt-3">{"Il codice verrà inserito dall'amministratore."}</p>
            )}
          </div>
        </section>

        {/* App cards */}
        <section>
          <div className="text-xs uppercase tracking-widest text-white/50 mb-3">Le nostre applicazioni</div>
          <div className="space-y-3">
            {apps.map((app, i) => {
              const Icon = getIcon(app.icon_name);
              return (
                <motion.a
                  key={app.id}
                  href={app.url || DEFAULT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i }}
                  data-testid={`app-card-${app.id}`}
                  className="card-surface p-5 flex items-center gap-5 relative overflow-hidden block group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[var(--gold)]/15 to-transparent opacity-60 pointer-events-none" />
                  <div className="relative w-14 h-14 rounded-2xl bg-[var(--bg-elevated)] border border-[var(--gold)]/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {app.logo_url ? (
                      <img src={mediaUrl(app.logo_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Icon className="w-7 h-7 text-[var(--gold)]" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="relative flex-1 min-w-0 py-1">
                    <div className="font-heading font-medium text-white text-base leading-tight">{app.name}</div>
                    {app.tagline && (
                      <div className="text-xs text-white/60 mt-2 leading-relaxed">{app.tagline}</div>
                    )}
                  </div>
                  <div
                    data-testid={`download-btn-${app.id}`}
                    className="relative w-12 h-12 rounded-full bg-[var(--gold)] text-black flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform"
                    style={{ boxShadow: '0 6px 18px rgba(212,175,55,0.35), 0 2px 6px rgba(0,0,0,0.35)' }}
                  >
                    <Download className="w-5 h-5" strokeWidth={2.4} />
                  </div>
                </motion.a>
              );
            })}
            {apps.length === 0 && (
              <div className="text-center text-white/40 text-sm py-6">Nessuna app disponibile.</div>
            )}
          </div>
        </section>

        {/* Installation guide */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-4 h-4 text-[var(--gold)]" />
            <div className="text-xs uppercase tracking-widest text-white/50">Guida installazione</div>
          </div>
          <ol className="space-y-2 text-sm">
            {[
              "Apri l'app \"Downloader\" sul tuo dispositivo Android o Fire TV.",
              'Inserisci il Codice Downloader mostrato sopra (o copialo con un tap).',
              'Attendi il completamento del download del file APK.',
              'Alla richiesta consenti l\'installazione da fonti sconosciute.',
              'Installa e apri l\'applicazione: è tutto pronto!',
            ].map((t, i) => (
              <li key={i} className="card-surface p-3 flex gap-3 items-start">
                <div className="w-7 h-7 rounded-full bg-[var(--gold-soft)] text-[var(--gold)] flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {i + 1}
                </div>
                <span className="text-white/80 pt-0.5">{t}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* Open GitHub download page */}
        <a
          data-testid="open-download-page-btn"
          href={urls.download_page_url || DEFAULT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold w-full py-4 flex items-center justify-center gap-2"
        >
          <Download className="w-5 h-5" />
          Apri pagina Download
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </Layout>
  );
}
